/**
 * Client für Zähler und Mieteinheiten eines Gebäudes (tri-server docs/metering.md).
 */
import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export type Medium = "electricity" | "water" | "gas" | "heat" | "cold" | "compressedAir" | "other";
export const MEDIA: Medium[] = ["electricity", "water", "gas", "heat", "cold", "compressedAir", "other"];

export interface MeterNode {
  _id: string;
  key: string;
  name: string;
  medium: Medium;
  upstreamMeter: string | null;
  meteredUnit: string | null;
  meteredObjectType: "Floor" | "Space" | "Asset" | null;
  meteredObject: string | null;
  /** Name der gemessenen Anlage (vom Server aufgelöst). */
  meteredObjectName?: string | null;
  schemaCategory: string | null;
  meterNumber: string | null;
  /** Liefervertrag (EnergySupplyContract) am Zähler selbst; Unterzähler erben den ihres Hauptzählers. */
  supplyContract: string | null;
  /** Zähler-Attribute aus Asset.properties (Schlüssel der Taxonomie) und mitgelesene Importfelder. */
  attributes: Record<string, any>;
  nextCalibration: { at: string; years: number | null; source: "date" | "computed" } | null;
  consumption: number | null;
  rest: number | null;
  lastReading: { at: string; value: number } | null;
  children: MeterNode[];
}

export interface MeterTree {
  period: { from: string; to: string };
  units: Record<Medium, string>;
  media: Partial<Record<Medium, MeterNode[]>>;
  count: number;
}

export interface RentalUnit {
  _id: string;
  name: string;
  floors: string[];
  spaces: string[];
  grossFloorArea: number | null;
  computedArea: number | null;
  meters: Array<{ _id: string; name: string; medium: Medium; top: boolean }>;
  consumption: Partial<Record<Medium, number | null>>;
  /** Mietverträge der Einheit mit Mieter und Vermieter. */
  leases: RentalLease[];
}

export interface RentalLease {
  _id: string;
  contractNumber: string | null;
  startDate: string | null;
  endDate: string | null;
  rent: { amount?: number; currency?: string } | null;
  permittedUse: string | null;
  tenant: { _id: string; name: string | null } | null;
  landlord: { _id: string; name: string | null } | null;
  status: "active" | "upcoming" | "ended";
}

export interface RentalUnitsView {
  period: { from: string; to: string };
  units: Record<Medium, string>;
  floors: Array<{ _id: string; name: string; level: number; grossFloorArea: number | null }>;
  spaces: Array<{ _id: string; name: string; floor: string; floorArea: number | null }>;
  rentalUnits: RentalUnit[];
}

export interface MeterSuggestion {
  meter: string;
  meterName: string;
  upstream: string;
  upstreamName: string;
  medium: Medium;
  confidence: "high";
  reason: string;
}
export interface MeterHint {
  kind: "groupWithoutMain" | "waterRoots";
  medium: Medium;
  text: string;
  meters: string[];
}

export interface MeterDashboard {
  medium: Medium;
  unit: string;
  period: { from: string; to: string; previousFrom: string };
  resolution: "15min" | "hour" | "day";
  node: { id: string; name: string } | null;
  nodes: Array<{ id: string; name: string; depth: number; children: number }>;
  kpis: {
    consumption: number | null;
    previous: number | null;
    change: number | null;
    peakHour: number | null;
    peakAt: string | null;
    peak15: number | null;
    baseLoad: number | null;
    baseShare: number | null;
    unmetered: number | null;
    completeness: number | null;
    specific: number | null;
    area: number | null;
    /** t CO₂(e) im Zeitraum, marktbasiert (Liefervertrag) bzw. standortbasiert (UBA/BAFA). */
    co2Market: number | null;
    co2Location: number | null;
    /** kg CO₂(e) je m², marktbasiert. */
    co2Intensity: number | null;
    co2Notes: Array<"ownGeneration" | "gasUnit" | "noFactor" | "fallback" | "contract">;
    co2Year: number;
    co2Sources: string[];
    /** Energiekosten netto im Zeitraum (Arbeitspreis × Verbrauch + Grundpreis anteilig); null ohne Preise. */
    cost: number | null;
    costEnergy: number | null;
    costBase: number | null;
    costCurrency: string | null;
  };
  /** g CO₂(e) je Einheit des Mediums für jede Lastgang-Reihe bzw. jeden Sankey-Knoten. */
  seriesFactors: Record<string, Co2Factors>;
  flowFactors: Record<string, Co2Factors>;
  series: Array<{ id: string; name: string; data: Array<[number, number]> }>;
  heatmap: Array<{ day: string; hours: Array<number | null> }>;
  signature: { model: "heating" | "cooling" | "linear"; base: number; slope: number; balance: number | null; r2: number; line: Array<{ t: number; value: number }> } | null;
  signaturePoints: Array<{ day: string; t: number; value: number; dayType: "workday" | "saturday" | "sunday" }>;
  typicalDay: Record<"workday" | "saturday" | "sunday", Array<number | null>>;
  ranking: Array<{ id: string; name: string; consumption: number | null; previous: number | null; trend: number | null; share: number | null; flags: string[]; hasChildren: boolean }>;
  withoutData: number;
  flow: {
    energy: { nodes: Array<{ id: string; name: string; kind: "source" | "meter" | "rest" | "unit" | "common" }>; links: Array<{ source: string; target: string; value: number }> };
    tenants: { nodes: Array<{ id: string; name: string; kind: "source" | "meter" | "rest" | "unit" | "common" }>; links: Array<{ source: string; target: string; value: number }>; assignedMeters: number };
  };
}

export interface Co2Factors {
  market: number | null;
  location: number | null;
}

export type EnergyCarrier = "electricity" | "naturalGas" | "biomethane" | "districtHeating" | "districtCooling" | "heatingOil" | "woodPellets" | "lpg" | "other";
export const ENERGY_CARRIERS: EnergyCarrier[] = ["electricity", "naturalGas", "biomethane", "districtHeating", "districtCooling", "heatingOil", "woodPellets", "lpg", "other"];

export interface SupplyContract {
  _id: string;
  contractNumber: string | null;
  energyCarrier: EnergyCarrier | null;
  productName: string | null;
  supplierName: string | null;
  startDate: string | null;
  endDate: string | null;
  marketLocationIds: string | null;
  meteringLocationIds: string | null;
  supplyAddress: string | null;
  annualQuantity: number | null;
  /** g CO₂(e)/kWh laut Stromkennzeichnung bzw. Versorger. */
  emissionFactorMarket: number | null;
  emissionFactorYear: number | null;
  emissionFactorSource: string | null;
  renewableShare: number | null;
  guaranteesOfOrigin: boolean | null;
  energyPrice: number | null;
  /** Grundpreis je Jahr (netto). */
  basePrice: number | null;
  /** Gas: Abrechnungsbrennwert (kWh/m³) und Zustandszahl — rechnen Gaszähler in m³ in kWh um. */
  calorificValue: number | null;
  gasStateFactor: number | null;
  /** Aus einem Dokument des Datenraums erkannt. */
  fromDocument: boolean;
  meters: Array<{ _id: string; name: string; medium: Medium }>;
}

export interface ImportReport {
  error?: string;
  rows: number;
  accepted: number;
  written?: number;
  applied?: boolean;
  issues: Array<{ line: number; meter: string; problem: string }>;
  warnings: Array<{ line: number; meter: string; problem: string }>;
}

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    message = body.message || body.error || message;
  } catch {
    // kein JSON
  }
  throw new ApplicationError(message);
};

export const useMeteringAPI = () => {
  const base = configuration.serverUrl;
  const json = async (path: string, init: RequestInit = {}) =>
    processResponse(await authenticatedFetch(`${base}${path}`, { ...init, headers: { "Content-Type": "application/json" } }));
  return {
    meters: (building: string): Promise<MeterTree> => json(`/buildings/${encodeURIComponent(building)}/meters`),
    updateMeter: (asset: string, patch: Partial<Pick<MeterNode, "upstreamMeter" | "meteredUnit" | "meteredObjectType" | "meteredObject" | "supplyContract">> & { meterMedium?: Medium; attributes?: Record<string, unknown> }) =>
      json(`/meters/${encodeURIComponent(asset)}`, { method: "PATCH", body: JSON.stringify(patch) }),
    suggestions: (building: string): Promise<{ suggestions: MeterSuggestion[]; hints: MeterHint[] }> =>
      json(`/buildings/${encodeURIComponent(building)}/meter-suggestions`),
    applySuggestions: (building: string, pairs: Array<{ meter: string; upstream: string }>): Promise<{ applied: number; failed: Array<{ meter: string; error: string }> }> =>
      json(`/buildings/${encodeURIComponent(building)}/meter-suggestions`, { method: "POST", body: JSON.stringify({ pairs }) }),
    searchAssets: (building: string, q: string): Promise<Array<{ _id: string; name: string; schemaCategory: string | null; isMeter: boolean }>> =>
      json(`/buildings/${encodeURIComponent(building)}/asset-search?q=${encodeURIComponent(q)}`),
    dashboard: (building: string, q: { medium: Medium; node?: string | null; from: Date; to: Date }): Promise<MeterDashboard> =>
      json(
        `/buildings/${encodeURIComponent(building)}/meter-dashboard?medium=${q.medium}&from=${q.from.toISOString()}&to=${q.to.toISOString()}${q.node ? `&node=${encodeURIComponent(q.node)}` : ""}`
      ),
    rentalUnits: (building: string): Promise<RentalUnitsView> => json(`/buildings/${encodeURIComponent(building)}/rental-units`),
    saveRentalUnit: (building: string, unit: Partial<RentalUnit>, id?: string) =>
      json(`/buildings/${encodeURIComponent(building)}/rental-units${id ? `/${encodeURIComponent(id)}` : ""}`, {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(unit),
      }),
    deleteRentalUnit: (building: string, id: string) =>
      json(`/buildings/${encodeURIComponent(building)}/rental-units/${encodeURIComponent(id)}`, { method: "DELETE" }),
    supplyContracts: (building: string): Promise<SupplyContract[]> => json(`/buildings/${encodeURIComponent(building)}/supply-contracts`),
    saveSupplyContract: (building: string, contract: Record<string, unknown>, id?: string): Promise<{ _id: string }> =>
      json(`/buildings/${encodeURIComponent(building)}/supply-contracts${id ? `/${encodeURIComponent(id)}` : ""}`, {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(contract),
      }),
    deleteSupplyContract: (building: string, id: string) =>
      json(`/buildings/${encodeURIComponent(building)}/supply-contracts/${encodeURIComponent(id)}`, { method: "DELETE" }),
    uploadReadings: async (building: string, file: File, apply: boolean): Promise<ImportReport> => {
      const form = new FormData();
      form.append("file", file);
      return processResponse(
        await authenticatedFetch(`${base}/buildings/${encodeURIComponent(building)}/meter-readings${apply ? "?apply=1" : ""}`, { method: "POST", body: form })
      );
    },
  };
};
